import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import { usePageMeta, PAGE_META } from '../hooks/usePageMeta';
import { 
  Receipt, 
  Monitor, 
  ExternalLink, 
  Clock, 
  ArrowRight,
  Briefcase,
  FileText,
  Users,
  Settings,
  Package,
  Calculator,
  Shield,
  TrendingUp,
  Activity,
  ScanEye
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for sub-apps
const ICON_MAP = {
  Receipt: Receipt,
  Monitor: Monitor,
  Briefcase: Briefcase,
  FileText: FileText,
  Users: Users,
  Settings: Settings,
  Package: Package,
  Calculator: Calculator,
  Shield: Shield,
  Clock: Clock,
  TrendingUp: TrendingUp,
  Activity: Activity,
  ScanEye: ScanEye
};

const SubAppCard = ({ app, hasAccess, onLaunch }) => {
  const IconComponent = ICON_MAP[app.icon] || Package;
  
  return (
    <Card 
      className={`app-card relative overflow-hidden ${
        hasAccess ? 'cursor-pointer' : 'opacity-60'
      }`}
      data-testid={`app-card-${app.id}`}
    >
      {/* Accent stripe */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-secondary" />
      
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="p-3 rounded-lg bg-primary/10 text-primary">
            <IconComponent className="w-6 h-6" />
          </div>
          {!hasAccess && (
            <Badge variant="secondary" className="text-xs">
              No Access
            </Badge>
          )}
          {hasAccess && app.is_active && (
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
              Available
            </Badge>
          )}
        </div>
        <CardTitle className="text-lg mt-4">{app.name}</CardTitle>
        <CardDescription className="line-clamp-2">
          {app.description}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pt-0">
        {hasAccess ? (
          <Button 
            className="w-full gap-2 group"
            onClick={() => onLaunch(app)}
            data-testid={`launch-app-${app.id}`}
          >
            Launch App
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Button>
        ) : (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>Contact admin for access</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const LoadingSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {[1, 2, 3].map((i) => (
      <Card key={i} className="overflow-hidden">
        <div className="h-1 bg-slate-200" />
        <CardHeader className="pb-3">
          <Skeleton className="h-12 w-12 rounded-lg" />
          <Skeleton className="h-5 w-2/3 mt-4" />
          <Skeleton className="h-4 w-full mt-2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    ))}
  </div>
);

const Dashboard = () => {
  const { user, token, isSuperAdmin } = useAuth();
  const [subApps, setSubApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set page title and favicon for main dashboard
  usePageMeta(PAGE_META.MAIN_DASHBOARD.title, PAGE_META.MAIN_DASHBOARD.favicon);

  useEffect(() => {
    // Bug 6 Fix: Wait for token to be available before fetching
    if (token) {
      fetchSubApps();
    }
  }, [token]);

  const fetchSubApps = async () => {
    try {
      setLoading(true);
      // Bug 6 Fix: Include auth token in request header
      const response = await axios.get(`${API}/subapps`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      setSubApps(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load applications:', err);
      setError('Failed to load applications');
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const hasAppAccess = (appId) => {
    if (isSuperAdmin) return true;
    return user?.app_access?.includes(appId) || false;
  };

  const handleLaunchApp = (app) => {
    // Check if this is the Expense Manager
    if (app.route === '/apps/expense-manager') {
      // Open Expense Manager: it signs in from the session (useSessionLaunch), no token in the URL
      const appUrl = `${window.location.origin}/apps/expense-manager`;
      window.open(appUrl, '_blank');
      toast.success(`Launched ${app.name}`, {
        description: 'Opened in a new window'
      });
      return;
    }
    
    // Check if this is the Asset Manager
    if (app.route === '/apps/asset-manager') {
      // Open Asset Manager: it signs in from the session (useSessionLaunch), no token in the URL
      const appUrl = `${window.location.origin}/apps/asset-manager`;
      window.open(appUrl, '_blank');
      toast.success(`Launched ${app.name}`, {
        description: 'Opened in a new window'
      });
      return;
    }
    
    // Check if this is the Recruitment Portal
    if (app.route === '/apps/recruitment-portal') {
      // Open Recruitment Portal: it signs in from the session (useSessionLaunch), no token in the URL
      const appUrl = `${window.location.origin}/apps/recruitment-portal`;
      window.open(appUrl, '_blank');
      toast.success(`Launched ${app.name}`, {
        description: 'Opened in a new window'
      });
      return;
    }
    
    // Check if this is the Form Builder
    if (app.route === '/apps/form-builder') {
      // Open Form Builder: it signs in from the session (useSessionLaunch), no token in the URL
      const appUrl = `${window.location.origin}/apps/form-builder`;
      window.open(appUrl, '_blank');
      toast.success(`Launched ${app.name}`, {
        description: 'Opened in a new window'
      });
      return;
    }
    
    // Check if this is the Payroll Utilities
    if (app.route === '/apps/payroll-utilities') {
      // Open Payroll Utilities: it signs in from the session (useSessionLaunch), no token in the URL
      const appUrl = `${window.location.origin}/apps/payroll-utilities`;
      window.open(appUrl, '_blank');
      toast.success(`Launched ${app.name}`, {
        description: 'Opened in a new window'
      });
      return;
    }
    
    // Check if this is Harmony 2.0 Projects
    if (app.route === '/apps/harmony-projects') {
      const appUrl = `${window.location.origin}/apps/harmony-projects`;
      window.open(appUrl, '_blank');
      toast.success(`Launched ${app.name}`, {
        description: 'Opened in a new window'
      });
      return;
    }
    
    // Check if this is Harmony PhishPass
    if (app.route === '/apps/phishpass') {
      const appUrl = `${window.location.origin}/apps/phishpass`;
      window.open(appUrl, '_blank');
      toast.success(`Launched ${app.name}`, {
        description: 'Opened in a new window'
      });
      return;
    }
    
    // Harmony Timesheet — no token in URL. The sub-app bootstraps from
    // the HttpOnly refresh cookie (same origin, same profile → cookie
    // rides along) and pulls a fresh access token via /api/auth/refresh
    // on mount. Keeps the JWT out of the URL, out of server logs, out of
    // referrer headers, and out of browser history.
    if (app.route === '/apps/timesheet') {
      const appUrl = `${window.location.origin}/apps/timesheet`;
      window.open(appUrl, '_blank');
      toast.success(`Launched ${app.name}`, {
        description: 'Opened in a new window'
      });
      return;
    }

    // Harmony Growth — same cookie bootstrap as Timesheet, no token in URL.
    if (app.route === '/apps/harmony-growth') {
      const appUrl = `${window.location.origin}/apps/harmony-growth`;
      window.open(appUrl, '_blank');
      toast.success(`Launched ${app.name}`, {
        description: 'Opened in a new window'
      });
      return;
    }

    // Harmony Pulse — same cookie bootstrap, no token in URL.
    if (app.route === '/apps/harmony-pulse') {
      const appUrl = `${window.location.origin}/apps/harmony-pulse`;
      window.open(appUrl, '_blank');
      toast.success(`Launched ${app.name}`, {
        description: 'Opened in a new window'
      });
      return;
    }

    // Harmony Candor — same cookie bootstrap, no token in URL.
    if (app.route === '/apps/candor') {
      const appUrl = `${window.location.origin}/apps/candor`;
      window.open(appUrl, '_blank');
      toast.success(`Launched ${app.name}`, {
        description: 'Opened in a new window'
      });
      return;
    }
    
    // For other apps, show placeholder
    const newWindow = window.open('', '_blank');
    
    if (newWindow) {
      // Create a placeholder page for the sub-app
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${app.name} - Harmony Apps</title>
          <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@600;700&family=Inter:wght@400;500&display=swap" rel="stylesheet">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Inter', sans-serif; 
              background: linear-gradient(135deg, #F8FAFC 0%, #E3F2FD 100%);
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .container {
              text-align: center;
              padding: 3rem;
              background: white;
              border-radius: 16px;
              box-shadow: 0 10px 40px rgba(15, 82, 186, 0.1);
              max-width: 500px;
            }
            h1 { 
              font-family: 'Manrope', sans-serif;
              color: #0F52BA;
              margin-bottom: 1rem;
              font-size: 2rem;
            }
            p { 
              color: #64748B;
              line-height: 1.6;
            }
            .badge {
              display: inline-block;
              background: #FFC107;
              color: #1A1A1A;
              padding: 0.5rem 1rem;
              border-radius: 20px;
              font-weight: 500;
              margin-top: 1.5rem;
              font-size: 0.875rem;
            }
            .icon {
              width: 80px;
              height: 80px;
              background: linear-gradient(135deg, #0F52BA 0%, #0DC0FE 100%);
              border-radius: 20px;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 auto 1.5rem;
            }
            .icon svg {
              width: 40px;
              height: 40px;
              stroke: white;
              stroke-width: 1.5;
              fill: none;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="icon">
              <svg viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            </div>
            <h1>${app.name}</h1>
            <p>${app.description}</p>
            <span class="badge">Coming Soon</span>
          </div>
        </body>
        </html>
      `);
      newWindow.document.close();
      
      toast.success(`Launched ${app.name}`, {
        description: 'Opened in a new window'
      });
    } else {
      toast.error('Unable to open new window', {
        description: 'Please check your popup blocker settings'
      });
    }
  };

  // Separate apps into accessible and inaccessible
  const accessibleApps = subApps.filter(app => hasAppAccess(app.id) && app.is_active);
  const otherApps = subApps.filter(app => !hasAppAccess(app.id) || !app.is_active);

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">
            Welcome back, {user?.full_name?.split(' ')[0] || user?.username}
          </h1>
          <p className="text-muted-foreground">
            Select an application to get started
          </p>
        </div>

        {/* Loading State */}
        {loading && <LoadingSkeleton />}

        {/* Error State */}
        {error && !loading && (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="pt-6">
              <p className="text-destructive">{error}</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={fetchSubApps}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Apps Grid */}
        {!loading && !error && (
          <>
            {/* Accessible Apps */}
            {accessibleApps.length > 0 && (
              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  Your Applications
                  <Badge variant="secondary" className="font-normal">
                    {accessibleApps.length}
                  </Badge>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {accessibleApps.map((app, index) => (
                    <div 
                      key={app.id} 
                      className="animate-slide-up"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <SubAppCard 
                        app={app} 
                        hasAccess={true}
                        onLaunch={handleLaunchApp}
                      />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Other Apps (No Access) */}
            {otherApps.length > 0 && !isSuperAdmin && (
              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-foreground">
                  Other Applications
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {otherApps.map((app, index) => (
                    <div 
                      key={app.id} 
                      className="animate-slide-up"
                      style={{ animationDelay: `${(accessibleApps.length + index) * 0.1}s` }}
                    >
                      <SubAppCard 
                        app={app} 
                        hasAccess={false}
                        onLaunch={handleLaunchApp}
                      />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Empty State */}
            {subApps.length === 0 && (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Package className="w-16 h-16 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Applications Available</h3>
                  <p className="text-muted-foreground text-center max-w-md">
                    There are no applications configured yet. Contact your administrator to set up applications.
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
